import React, { useEffect, useRef, useState, useCallback } from 'react';
import './LocationPickerModal.css';

const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM   = 5;

function PinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function CrosshairIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="8" />
      <line x1="12" y1="2" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="2" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="22" y2="12" />
    </svg>
  );
}

function CentrePin({ dragging }) {
  return (
    <div className={`lpm-centre-pin${dragging ? ' lpm-centre-pin--dragging' : ''}`}>
      <div className="lpm-centre-pin__shadow" />
      <svg className="lpm-centre-pin__svg" viewBox="0 0 40 56" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M20 2C11.163 2 4 9.163 4 18c0 11.25 14.25 32.5 15.25 34a.9.9 0 0 0 1.5 0C21.75 50.5 36 29.25 36 18 36 9.163 28.837 2 20 2z"
          fill="#2D6A4F" stroke="#fff" strokeWidth="2"
        />
        <circle cx="20" cy="18" r="6" fill="#fff" />
        <circle cx="20" cy="18" r="3" fill="#2D6A4F" />
      </svg>
    </div>
  );
}

/* ── Geocode a text query using Nominatim (OpenStreetMap, free) ── */
export async function geocodeAddress(query) {
  if (!query) return null;
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=in&limit=1`;
    const res  = await fetch(url, { headers: { 'Accept-Language': 'en' } });
    const data = await res.json();
    if (data && data[0]) {
      return {
        lat:   parseFloat(data[0].lat),
        lng:   parseFloat(data[0].lon),
        label: data[0].display_name,
      };
    }
  } catch (e) {
    console.warn('[geocodeAddress] Nominatim error:', e);
  }
  return null;
}

/* ── Reverse geocode lat/lng → address string (Nominatim) ── */
async function reverseGeocode(lat, lng) {
  try {
    const url  = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`;
    const res  = await fetch(url, { headers: { 'Accept-Language': 'en' } });
    const data = await res.json();
    return data?.display_name || '';
  } catch {
    return '';
  }
}

/* ── Build query string from form fields ── */
export function buildQuery(fa) {
  if (!fa) return null;
  const { address, city, state, pincode } = fa;
  if (!city && !pincode) return null;
  return [address, city, state, pincode, 'India'].filter(Boolean).join(', ');
}

/* ── Compatibility shim — kept so CheckoutPage imports don't break ── */
export function waitForGoogleMaps(callback) {
  setTimeout(callback, 0);
  return () => {};
}

/* ── Load + cache the Government-of-India-compliant national boundary
   (overlaid on top of the OSM base tiles, which show contested borders
   like J&K/Aksai Chin/Arunachal Pradesh per international convention
   rather than India's official boundary). ── */
let indiaBoundaryPromise = null;
function loadIndiaBoundary() {
  if (!indiaBoundaryPromise) {
    indiaBoundaryPromise = fetch('/india-boundary.geojson')
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);
  }
  return indiaBoundaryPromise;
}

function addIndiaBoundary(L, map) {
  loadIndiaBoundary().then((geojson) => {
    if (!geojson) return;
    L.geoJSON(geojson, {
      style: {
        color: '#0B3D91',   // official-boundary line colour
        weight: 1.5,
        opacity: 0.85,
        fill: false,
      },
      interactive: false,   // don't intercept clicks/drag
    }).addTo(map);
  });
}

/* ── Load Leaflet CSS + JS from CDN once ── */
function loadLeaflet() {
  return new Promise((resolve) => {
    if (window.L) { resolve(); return; }

    if (!document.getElementById('leaflet-css')) {
      const link  = document.createElement('link');
      link.id     = 'leaflet-css';
      link.rel    = 'stylesheet';
      link.href   = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    const script    = document.createElement('script');
    script.src      = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload   = resolve;
    script.onerror  = resolve;
    document.head.appendChild(script);
  });
}

/* ════════════════════════════════════════════════════
   LocationPickerModal — Leaflet / OpenStreetMap version
   ════════════════════════════════════════════════════ */
export default function LocationPickerModal({
  open, onClose, onConfirm, initialLocation, formAddress, prefetchedLocation,
}) {
  const mapDivRef       = useRef(null);
  const mapRef          = useRef(null);
  const reverseTimerRef = useRef(null);

  const [dragging,     setDragging]     = useState(false);
  const [coords,       setCoords]       = useState(null);
  const [locating,     setLocating]     = useState(false);
  const [geoError,     setGeoError]     = useState('');
  const [mapsError,    setMapsError]    = useState('');
  const [showBanner,   setShowBanner]   = useState(false);
  const [addressLabel, setAddressLabel] = useState('');

  /* ── GPS button ── */
  const useCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError('Location services not supported by your browser.');
      return;
    }

    setLocating(true);
    setGeoError('');
    setShowBanner(false);

    const apply = (lat, lng) => {
      if (!mapRef.current) return;
      mapRef.current.setView([lat, lng], 16);
      setCoords({ lat, lng });
      setLocating(false);
    };

    const fail = (err) => {
      setLocating(false);
      if (err && err.code === 1) {
        setGeoError(
          'Location access blocked. Click the 🔒 icon in the address bar → Site settings → Location → Allow, then try again.'
        );
      } else if (err && err.code === 2) {
        setGeoError("Your location couldn't be determined. Try again or move to a better signal area.");
      } else if (err && err.code === 3) {
        setGeoError('Location request timed out. Please try again.');
      } else {
        setGeoError("Couldn't access your location. Please try again.");
      }
    };

    // Fast low-accuracy first, then high-accuracy fallback
    navigator.geolocation.getCurrentPosition(
      (p) => apply(p.coords.latitude, p.coords.longitude),
      (err) => {
        // Permission denied — no point retrying
        if (err && err.code === 1) { fail(err); return; }
        navigator.geolocation.getCurrentPosition(
          (p) => apply(p.coords.latitude, p.coords.longitude),
          fail,
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      },
      { enableHighAccuracy: false, timeout: 500, maximumAge: 60000 }
    );
  }, []);

  /* ── Main effect: init map when modal opens ── */
  useEffect(() => {
    if (!open) return;

    setGeoError('');
    setMapsError('');
    setShowBanner(false);

    let cancelled = false;

    (async () => {
      await loadLeaflet();
      if (cancelled || !mapDivRef.current) return;

      const L = window.L;
      if (!L) { setMapsError('Map library could not load. Check your internet connection.'); return; }

      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }

      let startLat        = DEFAULT_CENTER.lat;
      let startLng        = DEFAULT_CENTER.lng;
      let startZoom       = DEFAULT_ZOOM;
      let fromFormAddress = false;

      if (initialLocation?.lat != null) {
        startLat  = initialLocation.lat;
        startLng  = initialLocation.lng;
        startZoom = 15;
        setAddressLabel(initialLocation.address || '');
      } else if (prefetchedLocation?.lat != null) {
        startLat        = prefetchedLocation.lat;
        startLng        = prefetchedLocation.lng;
        startZoom       = 15;
        fromFormAddress = true;
        setAddressLabel(prefetchedLocation.label || '');
      } else {
        const q = buildQuery(formAddress);
        if (q) {
          const result = await geocodeAddress(q);
          if (result && !cancelled) {
            startLat        = result.lat;
            startLng        = result.lng;
            startZoom       = 15;
            fromFormAddress = true;
            setAddressLabel(result.label || '');
          }
        }
      }

      if (cancelled) return;

      const map = L.map(mapDivRef.current, {
        center:             [startLat, startLng],
        zoom:               startZoom,
        zoomControl:        true,
        attributionControl: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);

      addIndiaBoundary(L, map);

      mapRef.current = map;
      setCoords({ lat: startLat, lng: startLng });
      if (fromFormAddress) setShowBanner(true);

      map.on('movestart', () => setDragging(true));
      map.on('moveend', () => {
        setDragging(false);
        const c = map.getCenter();
        setCoords({ lat: c.lat, lng: c.lng });

        if (reverseTimerRef.current) clearTimeout(reverseTimerRef.current);
        reverseTimerRef.current = setTimeout(async () => {
          const label = await reverseGeocode(c.lat, c.lng);
          if (label) setAddressLabel(label);
        }, 400);
      });
    })();

    return () => {
      cancelled = true;
      if (reverseTimerRef.current) clearTimeout(reverseTimerRef.current);
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [open, formAddress, initialLocation, prefetchedLocation]); // eslint-disable-line

  const handleConfirm = () => {
    if (!coords) return;
    onConfirm({ address: addressLabel, lat: coords.lat, lng: coords.lng });
  };

  if (!open) return null;

  return (
    <div className="lpm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lpm-modal">

        <div className="lpm-hd">
          <h3 className="lpm-hd__title">Choose delivery location</h3>
          <button type="button" className="lpm-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {showBanner && (
          <div className="lpm-hint">
            <PinIcon />
            <span>Showing approximate location based on your address. Drag the map to adjust.</span>
          </div>
        )}

        {mapsError && <p className="lpm-error">{mapsError}</p>}

        <button type="button" className="lpm-current-btn" onClick={useCurrentLocation} disabled={locating}>
          <CrosshairIcon />
          {locating ? 'Finding your location…' : 'Use my current location'}
        </button>

        {geoError && <p className="lpm-error">{geoError}</p>}

        <div className="lpm-map-wrap">
          <div className="lpm-map" ref={mapDivRef} />
          <CentrePin dragging={dragging} />
          <p className="lpm-map-hint">Pan &amp; zoom to position the pin exactly</p>
        </div>

        <div className="lpm-ft">
          <button type="button" className="lpm-btn lpm-btn--ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="lpm-btn lpm-btn--primary"
            onClick={handleConfirm}
            disabled={!coords}
          >
            Confirm location
          </button>
        </div>

      </div>
    </div>
  );
}